/**
 * ManageVPE Component
 * Clean version using Component + Hook + Service pattern
 *
 * ARCHITECTURE:
 * - Clean separation: UI components, state management, business logic, API calls
 * - Reusability: Each component can be used independently
 * - Testability: Each piece can be tested in isolation
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useBackButtonLogout } from '@/hooks/ea/useBackButtonLogout.ts';
import LogoutConfirmDialog from '../common/LogoutConfirmDialog.tsx';
import { useManageESState } from '@/hooks/ea/useManageESState';
import { useManageESOperations } from '@/hooks/ea/useManageESOperations';
import ManageVPEHeader from './ManageVPEHeader';
import VPETableSection from './VPETableSection';
import VPEFormDialog from './VPEFormDialog';

interface ManageVPEProps {
  onBack: () => void;
}

const ManageVPE: React.FC<ManageVPEProps> = ({ onBack }) => {
  // State Management Hook
  const { state, actions, computed } = useManageESState();

  // Operations Hook
  const operations = useManageESOperations(state, actions, computed);

  // Handle browser back button to show logout dialog
  useBackButtonLogout({
    onLogoutRequest: () => actions.setShowLogoutDialog(true)
  });

  return (
    <>
      <div className="min-h-screen bg-white relative">
        {/* Watermark */}
        <div className="absolute inset-0 pointer-events-none opacity-5 bg-gradient-to-br from-purple-100 to-pink-100">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-45">
            <div className="text-6xl font-bold text-gray-400">HBOX VPE</div>
          </div>
        </div>

        {/* Header */}
        <ManageVPEHeader onBack={onBack} onAddVPE={operations.handleAddClick} />

        {/* Main Content */}
        <main className="p-8 relative z-10">
          <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0 rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <VPETableSection
                paginatedVPEs={computed.paginatedVPEs}
                isLoading={state.isLoading}
                deleteButtonLoading={state.deleteButtonLoading}
                currentPage={state.currentPage}
                totalPages={computed.totalPages}
                rowsPerPage={state.rowsPerPage}
                startIndex={computed.startIndex}
                endIndex={computed.endIndex}
                totalVPEs={computed.totalVPEs}
                searchValue={state.searchValue}
                onSearchChange={actions.setSearchValue}
                onSearch={actions.handleSearch}
                onReload={actions.handleReload}
                onEditClick={operations.handleEditClick}
                onDeleteClick={operations.handleDeleteClick}
                onPageChange={operations.handlePageChange}
                onRowsPerPageChange={operations.handleRowsPerPageChange}
              />
            </CardContent>
          </Card>
        </main>

        {/* Add/Edit VPE Dialog */}
        <VPEFormDialog
          open={state.showDialog}
          onOpenChange={actions.setShowDialog}
          isEditMode={state.isEditMode}
          isCreateLoading={state.isCreateLoading}
          firstName={state.firstName}
          lastName={state.lastName}
          email={state.email}
          password={state.password}
          confirmPassword={state.confirmPassword}
          taskAssigned={state.taskAssigned}
          showPassword={state.showPassword}
          showConfirmPassword={state.showConfirmPassword}
          onFirstNameChange={actions.setFirstName}
          onLastNameChange={actions.setLastName}
          onEmailChange={actions.setEmail}
          onPasswordChange={actions.setPassword}
          onConfirmPasswordChange={actions.setConfirmPassword}
          onTaskAssignedChange={operations.handleTaskAssignedChange}
          onShowPasswordToggle={() => actions.setShowPassword(!state.showPassword)}
          onShowConfirmPasswordToggle={() => actions.setShowConfirmPassword(!state.showConfirmPassword)}
          onSubmit={operations.handleSubmit}
          onCancel={() => {
            actions.setShowDialog(false);
            actions.resetForm();
          }}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={state.showDeleteDialog} onOpenChange={actions.setShowDeleteDialog}>
          <AlertDialogContent className="bg-white rounded-2xl shadow-2xl border-0">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg font-semibold text-gray-800">Delete VPE</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-600">
                Are you sure you want to delete {state.selectedVPE?.es_name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-lg border-gray-300 hover:bg-gray-50">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={operations.handleDelete}
                disabled={state.isDeleteLoading}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {state.isDeleteLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </div>
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Logout Confirmation Dialog */}
        <LogoutConfirmDialog
          open={state.showLogoutDialog}
          onOpenChange={actions.setShowLogoutDialog}
          onConfirm={operations.handleLogout}
        />
      </div>
    </>
  );
};

export default ManageVPE;